import { useForm } from '@tanstack/react-form'
import { capitalize } from 'lodash'
import { FilterIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { donationMethods } from '../constants/donationMethods'
import type { DonationStatus } from '../types/donation'
import type { DonationFilter } from '@/schemas/filterSchemas'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Badge } from '@/components/custom/badge'
import DatePicker from '@/components/custom/date-picker'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { useIsMobile } from '@/hooks'
import IconInput from '@/components/custom/icon-input'
import { parseLocalDateString, toLocalDateString } from '@/utils/timeZoneUtils'

export default function DonationFilter({
  onFilter,
  onCloseDrawer,
  initialFilters,
}: {
  onFilter?: (filters: DonationFilter) => void
  onCloseDrawer?: () => void
  initialFilters: DonationFilter
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  const churches = useChurchesStore((s) => s.churches)
  const members = useMembersStore((s) => s.members)

  const statusOptions = new Set<DonationStatus>(['pending', 'verified'])

  const form = useForm({
    defaultValues: {
      search: initialFilters.search,
      churchId: initialFilters.churchId,
      memberId: initialFilters.memberId,
      methods: initialFilters.methods,
      startDate: initialFilters.startDate,
      endDate: initialFilters.endDate,
      status: initialFilters.status,
      onlyActives: initialFilters.onlyActives,
    },
    onSubmit: async ({ value }) => {
      const startDateStr = value.startDate
        ? typeof value.startDate === 'string'
          ? value.startDate
          : toLocalDateString(value.startDate)
        : ''

      const endDateStr = value.endDate
        ? typeof value.endDate === 'string'
          ? value.endDate
          : toLocalDateString(value.endDate)
        : ''

      onFilter?.({
        ...value,
        startDate: startDateStr,
        endDate: endDateStr,
      })

      await new Promise((resolve) => setTimeout(resolve, 0))
      setOpen(false)
      if (isMobile && onCloseDrawer) onCloseDrawer()
    },
  })

  useEffect(() => {
    form.setFieldValue('churchId', initialFilters.churchId)
    form.setFieldValue('memberId', initialFilters.memberId)
    form.setFieldValue('methods', initialFilters.methods)
    form.setFieldValue('status', initialFilters.status)
    form.setFieldValue('onlyActives', initialFilters.onlyActives)
    form.setFieldValue('startDate', initialFilters.startDate)
    form.setFieldValue('endDate', initialFilters.endDate)
  }, [initialFilters])

  const handleClear = async () => {
    form.reset()

    onFilter?.({
      churchId: [],
      memberId: [],
      methods: [],
      startDate: '',
      endDate: '',
      status: '',
      onlyActives: false,
      search: isMobile ? '' : initialFilters.search,
    })
    await new Promise((resolve) => setTimeout(resolve, 0))
    setOpen(false)
  }

  const filtersActive =
    form.state.values.churchId.length > 0 ||
    form.state.values.memberId.length > 0 ||
    form.state.values.methods.length > 0 ||
    form.state.values.status ||
    form.state.values.startDate !== '' ||
    form.state.values.endDate !== '' ||
    form.state.values.onlyActives
  const activeCount = [
    form.state.values.churchId.length,
    form.state.values.memberId.length,
    form.state.values.methods.length,
    form.state.values.status ? 1 : 0,
    form.state.values.startDate !== '' ? 1 : 0,
    form.state.values.endDate !== '' ? 1 : 0,
    form.state.values.onlyActives ? 1 : 0,
  ].filter(Boolean).length

  const filterForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      {isMobile && (
        <form.Field
          name="search"
          children={(field) => (
            <IconInput
              placeholder="Search by donor or receipt #"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              className="w-full"
            />
          )}
        />
      )}
      <form.Field
        name="churchId"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Churches"
            id={field.name}
            name={field.name}
            options={churches.map((c) => ({
              value: c._id,
              label: c.name,
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="memberId"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Members"
            id={field.name}
            name={field.name}
            options={members.map((m) => ({
              value: m._id,
              label: `${m.name} (${m.email})`,
            }))}
            multiple
          />
        )}
      />
      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="startDate"
          children={(field) => (
            <DatePicker
              field={{
                ...field,
                state: {
                  ...field.state,
                  value: field.state.value
                    ? typeof field.state.value === 'string'
                      ? parseLocalDateString(field.state.value)
                      : field.state.value
                    : undefined,
                },
                handleChange: (date: Date | undefined) => {
                  const stringDate = date ? toLocalDateString(date) : ''
                  field.handleChange(stringDate)
                },
              }}
              label="Start Date"
              startYear={2000}
              disableFutureDates
              id={field.name}
              name={field.name}
              showClearButton
            />
          )}
        />
        <form.Subscribe
          selector={(state) => [state.values.startDate]}
          children={([startDateValue]) => (
            <form.Field
              name="endDate"
              children={(field) => (
                <DatePicker
                  field={{
                    ...field,
                    state: {
                      ...field.state,
                      value: field.state.value
                        ? typeof field.state.value === 'string'
                          ? parseLocalDateString(field.state.value)
                          : field.state.value
                        : undefined,
                    },
                    handleChange: (date: Date | undefined) => {
                      const stringDate = date ? toLocalDateString(date) : ''
                      field.handleChange(stringDate)
                    },
                  }}
                  label="End Date"
                  startYear={2000}
                  disableFutureDates
                  minDate={
                    startDateValue
                      ? parseLocalDateString(startDateValue)
                      : undefined
                  }
                  id={field.name}
                  name={field.name}
                  showClearButton
                />
              )}
            />
          )}
        />
      </div>

      <form.Field
        name="methods"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Donation Method"
            id={field.name}
            name={field.name}
            options={Array.from(donationMethods).map((m) => ({
              value: m,
              label: capitalize(m),
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="status"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Donation Status"
            id={field.name}
            name={field.name}
            options={Array.from(statusOptions).map((s) => ({
              value: s,
              label: capitalize(s),
            }))}
            clearable
          />
        )}
      />
      <form.Field
        name="onlyActives"
        children={(field) => {
          return (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={field.name}
                name={field.name}
                checked={field.state.value}
                onChange={(e) => field.handleChange(e.target.checked)}
                className="accent-primary h-4 w-4"
              />
              <label
                htmlFor={field.name}
                className="text-sm select-none cursor-pointer"
              >
                Show only active members
              </label>
            </div>
          )
        }}
      />
      <form.Subscribe
        selector={(state) => [
          state.canSubmit,
          state.values.churchId,
          state.values.memberId,
          state.values.methods,
          state.values.startDate,
          state.values.endDate,
          state.values.status,
          state.values.onlyActives,
          state.values.search,
        ]}
        children={([
          canSubmit,
          churchIdValue,
          memberIdValue,
          methodsValue,
          startDateValue,
          endDateValue,
          statusValue,
          onlyActivesValue,
          searchValue,
        ]) => {
          const hasFilter =
            (Array.isArray(churchIdValue) && churchIdValue.length) ||
            (Array.isArray(memberIdValue) && memberIdValue.length) ||
            (Array.isArray(methodsValue) && methodsValue.length) ||
            statusValue !== '' ||
            startDateValue ||
            endDateValue ||
            onlyActivesValue
          const hasMobileSearch = isMobile && searchValue
          return (
            <div
              className={
                !isMobile ? 'flex gap-2 justify-end pt-2' : 'space-y-2'
              }
            >
              <Button type="button" variant="secondary" onClick={handleClear}>
                Clear
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || (!hasFilter && !hasMobileSearch)}
              >
                Apply
              </Button>
            </div>
          )
        }}
      />
    </form>
  )

  if (isMobile) {
    return <div className="w-full">{filterForm}</div>
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={filtersActive ? 'relative' : ''}>
          <FilterIcon size={16} />
          Filters
          {filtersActive && <Badge color="red">{activeCount}</Badge>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-4 min-w-[320px] max-w-100">
        {filterForm}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
